import { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import './InvestmentSolution.css';

const InvestmentSolution = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>Investment Solution — Azamra</title>
        <meta name="description" content="Grow your wealth with Azamra's investment solutions. Access diversified portfolios and financial instruments designed for Africa's emerging markets." />
        <meta property="og:title" content="Investment Solution — Azamra" />
        <meta property="og:description" content="Grow your wealth with Azamra's investment solutions. Access diversified portfolios and financial instruments designed for Africa's emerging markets." />
        <meta property="og:url" content="https://www.azamra.com/investment-solution" />
        <link rel="canonical" href="https://www.azamra.com/investment-solution" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FinancialProduct",
            "name": "Azamra Investment Solution",
            "description": "Diversified investment portfolios and financial instruments for Africa's emerging markets.",
            "provider": { "@type": "Organization", "name": "Azamra" }
          }
        `}</script>
      </Helmet>
      <Header />

      {/* ── SECTION 1: HERO (Option B — Left-Aligned Full Screen) ── */}
      <section className="inv-hero-section" style={{ backgroundImage: `url('/investment solution background.webp')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="inv-hero-bg-overlay" />
        <Container className="inv-hero-content">
          <Row>
            <Col lg={7} md={10} xs={12}>
              <div className="inv-hero-glass-card">
                <h1 className="inv-hero-title">
                  Where Tokenization Meets African Opportunities
                </h1>
                <p className="inv-hero-subtitle">
                  Azamra brings tokenization to African capital markets using blockchain
                  infrastructure to unlock fair access, attract institutional capital, and raise
                  the standard for liquidity, transparency, and efficiency.
                </p>
                <p className="inv-hero-subtitle inv-hero-subtitle-secondary">
                  We are building the next generation of African capital markets: modern,
                  connected, and designed for scale.
                </p>
                <button className="inv-hero-btn" onClick={() => navigate('/contact')}>
                  Get Started
                </button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── SECTION 2: INFRASTRUCTURE THAT UNLOCKS CAPITAL ── */}
      <section className="inv-infra-section">
        <Container>
          <Row className="align-items-center inv-infra-row">

            {/* Left: Text */}
            <Col lg={6} md={12} className="inv-infra-left">
              <h2 className="inv-infra-title">Infrastructure That Unlocks Capital</h2>
              <p className="inv-infra-para">
                African markets have long been constrained by opacity, illiquidity,
                fragmented access, and slow settlement.
              </p>
              <p className="inv-infra-para">
                Azamra replaces these legacy barriers with clear, efficient, and
                programmable infrastructure. By embedding tokenization directly into
                capital markets, we open new funding pathways that move faster, settle
                instantly, and operate with full transparency.
              </p>
              <p className="inv-infra-para inv-infra-para-emphasis">
                This is infrastructure built for trust, speed, and growth.
              </p>
            </Col>

            {/* Right: Visual */}
            <Col lg={6} md={12} className="inv-infra-right">
              <div className="inv-infra-img-wrap">
                <img
                  src="/infrastructure.webp"
                  alt="Infrastructure"
                  className="inv-infra-img"
                />
              </div>
            </Col>

          </Row>

          <div className="inv-section-divider" />
        </Container>
      </section>

      {/* ── SECTION 3: DIGITAL SECURITIES INFRASTRUCTURE (Timeline) ── */}
      <section
        className="inv-digital-section"
        style={{ backgroundImage: `url('/assets/images/hero-bg1.webp')` }}
      >
        <Container>
          <Row>
            <Col xs={12} className="text-center">
              <h2 className="inv-digital-title">Digital Securities Infrastructure</h2>
              <p className="inv-digital-subtitle">The Platform Powering Transformation</p>
              <p className="inv-digital-desc">
                Azamra transforms traditional financial instruments into digital securities
                that move seamlessly across markets.
              </p>
            </Col>
          </Row>

          {/* Timeline */}
          <div className="inv-timeline">

            {/* Connecting line */}
            <div className="inv-timeline-line" />

            <Row className="inv-timeline-row">

              {/* Step 1 */}
              <Col lg={4} md={12} className="inv-timeline-col">
                <div className="inv-timeline-step">
                  <div className="inv-timeline-node">
                    <span className="inv-timeline-number">01</span>
                  </div>
                  <div className="inv-timeline-connector" />
                  <div className="inv-timeline-card">
                    <div className="inv-timeline-icon-wrap">
                      <img src="/assets/images/icon.webp" alt="Issuance" className="inv-timeline-icon" />
                    </div>
                    <h3 className="inv-timeline-card-title">Issuance</h3>
                    <p className="inv-timeline-card-desc">
                      Sovereign and corporate debt, real‑world assets, and equity become
                      liquid, accessible, and borderless. African issuers can raise capital
                      globally, 24/7.
                    </p>
                  </div>
                </div>
              </Col>

              {/* Step 2 */}
              <Col lg={4} md={12} className="inv-timeline-col">
                <div className="inv-timeline-step inv-timeline-step-mid">
                  <div className="inv-timeline-node inv-timeline-node-gold">
                    <span className="inv-timeline-number">02</span>
                  </div>
                  <div className="inv-timeline-connector" />
                  <div className="inv-timeline-card inv-timeline-card-gold">
                    <div className="inv-timeline-icon-wrap">
                      <img src="/assets/images/icon9.webp" alt="Trading & Settlement" className="inv-timeline-icon" />
                    </div>
                    <h3 className="inv-timeline-card-title">Trading & Settlement</h3>
                    <p className="inv-timeline-card-desc">
                      Full digital market stack — trading, settlement, custody, and
                      compliance. Every transaction secure, efficient, and ready for
                      institutional participation.
                    </p>
                  </div>
                </div>
              </Col>

              {/* Step 3 */}
              <Col lg={4} md={12} className="inv-timeline-col">
                <div className="inv-timeline-step">
                  <div className="inv-timeline-node">
                    <span className="inv-timeline-number">03</span>
                  </div>
                  <div className="inv-timeline-connector" />
                  <div className="inv-timeline-card">
                    <div className="inv-timeline-icon-wrap">
                      <img src="/assets/images/icon6.webp" alt="Custody & Compliance" className="inv-timeline-icon" />
                    </div>
                    <h3 className="inv-timeline-card-title">Custody & Compliance</h3>
                    <p className="inv-timeline-card-desc">
                      Every transaction is verifiable, compliant, and built to institutional
                      standards — secure infrastructure for the most demanding participants.
                    </p>
                  </div>
                </div>
              </Col>

            </Row>
          </div>
        </Container>
      </section>

      {/* ── SECTION 4: INSTITUTIONAL ACCESS ── */}
      <section className="inv-access-section">
        <Container>
          <Row className="align-items-start inv-access-row">

            {/* Left label */}
            <Col lg={4} md={12} className="inv-access-left">
              <h2 className="inv-access-title">Institutional Access.</h2>
              <p className="inv-access-subtitle">Direct and Transparent.</p>
            </Col>

            {/* Right large text */}
            <Col lg={8} md={12} className="inv-access-right">
              <p className="inv-access-text">
                Our infrastructure connects African issuers directly to global institutional
                capital{' '}
                <span className="inv-access-highlight">
                  without unnecessary intermediaries or outdated processes.
                </span>
              </p>
              <p className="inv-access-text inv-access-text-secondary">
                Institutions gain clarity and confidence. Issuers gain speed and reach.{' '}
                <span className="inv-access-highlight">
                  Africa gains faster, more reliable capital formation.
                </span>
              </p>
              <p className="inv-access-text inv-access-text-tertiary">
                The distance between African opportunity and global markets is now measured
                in seconds — not systems.
              </p>
              <button
                className="inv-access-btn"
                onClick={() => navigate('/contact')}
              >
                Get Started
              </button>
            </Col>

          </Row>
        </Container>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="inv-cta-section">
        <Container>
          <div
            className="inv-cta-banner"
            style={{ backgroundImage: `url('/assets/images/${isMobile ? 'cta1.webp' : 'cta.webp'}')` }}
          >
            <h2 className="inv-cta-title">
              <span className="inv-cta-title-bold">Azamra provides the secure infrastructure to</span>
              <span className="inv-cta-title-regular">launch, manage, and scale tokenized assets globally</span>
            </h2>
            <div className="inv-cta-badges">
              {/* <a href="#app-store">
                <img src="/assets/images/app-store-badge.webp" alt="Download on the App Store" className="inv-cta-badge" />
              </a>
              <a href="#google-play">
                <img src="/assets/images/google-play-badge.webp" alt="Get it on Google Play" className="inv-cta-badge" />
              </a> */}
              <a href="/contact" className="cta-demo-btn">Book a Demo</a>
            </div>
          </div>
        </Container>
      </section>

      <Footer />
    </>
  );
};

export default InvestmentSolution;
